import { useState } from "react";
import { Mail, Instagram, Send, Loader2, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePublicSettings } from "@/lib/usePublicSettings";
import { gaEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function ContactButtons({ contact_email, instagram_url, context }) {
  if (!contact_email && !instagram_url) return null;
  return (
    <div className="flex flex-wrap gap-3">
      {contact_email && (
        <a href={`mailto:${contact_email}?subject=${encodeURIComponent(context ? `Question about ${context}` : "Question for the instructor")}`}
          data-testid="email-instructor-btn"
          className="inline-flex items-center gap-2 rounded-sm bg-[#DCA846] px-5 py-2.5 font-display text-sm uppercase tracking-wide text-black transition-colors hover:bg-[#C5963B]">
          <Mail className="h-4 w-4" /> Email Instructor
        </a>
      )}
      {instagram_url && (
        <a href={instagram_url} target="_blank" rel="noreferrer"
          data-testid="instagram-instructor-btn"
          onClick={() => gaEvent("instagram_click", { link_url: instagram_url, source: "contact_instructor" })}
          className="inline-flex items-center gap-2 rounded-sm border border-white/20 px-5 py-2.5 font-display text-sm uppercase tracking-wide text-white transition-colors hover:border-[#DCA846]/50 hover:bg-white/5">
          <Instagram className="h-4 w-4" /> Instagram
        </a>
      )}
    </div>
  );
}

export function ContactInstructor({ context = "", course = "", lesson = "", variant = "card" }) {
  const { user } = useAuth();
  const { contact_email, instagram_url } = usePublicSettings();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const hasButtons = !!(contact_email || instagram_url);

  // Buttons-only variant (public: instructor section, marketing spots)
  if (variant === "buttons") {
    return <ContactButtons contact_email={contact_email} instagram_url={instagram_url} context={context} />;
  }

  // Full card: hide entirely if there is nothing to show (no buttons and not logged in)
  if (!hasButtons && !user) return null;

  const submit = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Please add a subject and message"); return; }
    setBusy(true);
    try {
      await api.post("/contact/instructor", { subject, message, context, course, lesson });
      toast.success("Message sent! The instructor will get back to you.");
      setSubject(""); setMessage("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send your message");
    } finally { setBusy(false); }
  };

  const wrap = variant === "bare" ? "" : "rounded-2xl border border-[#DCA846]/20 bg-gradient-to-br from-[#DCA846]/8 via-black/40 to-transparent p-6 sm:p-7";

  return (
    <div data-testid="contact-instructor" className={wrap}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#DCA846]/30 text-[#DCA846]">
          <MessageSquare className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-display text-lg uppercase tracking-wide text-white">Contact Instructor</h3>
          <p className="text-sm text-zinc-400">Have a question? Reach out directly.</p>
        </div>
      </div>

      {hasButtons && (
        <div className="mt-5">
          <ContactButtons contact_email={contact_email} instagram_url={instagram_url} context={context} />
        </div>
      )}

      {user ? (
        <div className={`${hasButtons ? "mt-6 border-t border-white/10 pt-5" : "mt-5"}`}>
          <p className="mb-3 font-display text-sm uppercase tracking-wide text-[#DCA846]">Ask the Instructor</p>
          {context ? <p className="mb-3 text-xs text-zinc-500">Regarding: <span className="text-zinc-300">{context}</span></p> : null}
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase text-zinc-400">Subject</Label>
              <Input data-testid="contact-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder="What's this about?" className="mt-1 bg-zinc-900 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-xs uppercase text-zinc-400">Message</Label>
              <Textarea data-testid="contact-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your question or message..." className="mt-1 bg-zinc-900 border-white/10 text-white" />
            </div>
            <p className="text-xs text-zinc-500">Sent as <span className="text-zinc-300">{user.name}</span> ({user.email}).</p>
            <Button data-testid="contact-send-btn" onClick={submit} disabled={busy}
              className="bg-[#DCA846] text-black hover:bg-[#C5963B] font-display uppercase tracking-wide">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="mr-1.5 h-4 w-4" /> Send Message</>)}
            </Button>
          </div>
        </div>
      ) : (
        <p className="mt-4 text-sm text-zinc-500">Log in to send a message directly from the site.</p>
      )}
    </div>
  );
}
